/* eslint-disable space-before-function-paren */
/* eslint-disable func-names */
import * as d3 from 'd3';
import { BaseType, NumberValue } from 'd3';
import { exportSvgAsPng, ISharedD3ChartProps, keyValueTitleTooltip, placeTooltip, wrapLine } from '../../vis-shared';

import { Size, Margin } from '../../visTypes';

export interface IDatum {
  id: string;
  label: string;
  value: number;
}

export interface ISimulationNodeDatum extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  value: number;
}

export interface IBeeswarmConfig extends ISharedD3ChartProps {
  target: any;
  data: IDatum[];
  min?: number;
  max?: number;
  height?: number;
  width?: number;
  color?: (d: IDatum, beeswarm: Beeswarm) => string;
  focalId?: string | null;
  hoverTooltip?: (tooltip: d3.Selection<HTMLDivElement, any, any, any>, d: IDatum) => void;
  valueAccessor?: (d: IDatum) => number;
  markRadius?: number;
  nodeTooltip?: (tooltip: d3.Selection<HTMLDivElement, any, any, any>, node: IDatum, chart: Beeswarm) => void;
  onNodeClick?: (d: ISimulationNodeDatum, chart: Beeswarm) => void;
}

const css = `
text {
  font-family: 'Manrope', sans-serif;
}
`;

export class Beeswarm {
  target: any;
  size: Size;
  chartSize: Size;
  margin: Margin;
  svg: d3.Selection<any, any, any, any>;
  simulation!: d3.Simulation<ISimulationNodeDatum, undefined>;
  chartSelection: d3.Selection<SVGGElement, any, any, any>;
  swarmSelection?: d3.Selection<BaseType, ISimulationNodeDatum, any, any>;
  tooltip: d3.Selection<HTMLDivElement, any, any, any>;
  exportFileName: string;
  exportTitle: string | null;
  data: IDatum[];
  markRadius: number;
  markPadding: number;
  xScale: d3.ScaleLinear<number, number>;
  normalizeScale: d3.ScaleLinear<number, number>;
  xAxis: d3.Axis<NumberValue>;
  xAxisSelection: d3.Selection<SVGGElement, any, any, any>;
  focalId: string | null;
  focalNode: IDatum | null;
  color: (d: IDatum, beeswarm: Beeswarm) => string;
  valueAccessor: (d: IDatum) => number;
  nodeTooltip: ((tooltip: d3.Selection<HTMLDivElement, any, any, any>, node: IDatum, chart: Beeswarm) => void) | undefined;
  onNodeClick: ((d: ISimulationNodeDatum, chart: Beeswarm) => void) | undefined;

  constructor(config: IBeeswarmConfig) {
    this.target = config.target;
    this.exportFileName = config.exportFileName || 'Beeswarm';
    this.exportTitle = config.exportTitle || null;
    this.focalId = config.focalId || null;
    this.focalNode = null;
    if (this.focalId) {
      // @ts-ignore
      this.focalNode = config.data.find((d) => d.id === this.focalId) || null;
    }
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    const height = config.height || 200;
    this.size = {
      width,
      height,
    };
    // const rightMargin = 0; // this.calculateNeededMargin(config.nodes.map((d) => d.label));
    this.margin = {
      top: 25,
      bottom: 25,
      left: 25,
      right: 25,
    };
    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.svg = d3
      .select(this.target)
      .style('position', 'relative')
      .append('svg')
      .classed('beeswarm-chart', true)
      .attr('height', this.size.height)
      .attr('width', this.size.width);

    this.svg.append('style').text(css);

    this.chartSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.tooltip = d3
      .select('body')
      .append('div')
      .style('visibility', 'hidden')
      .style('width', '160px')
      .style('font-size', '0.8em')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', 150);

    this.nodeTooltip = config.nodeTooltip;
    this.onNodeClick = config.onNodeClick;

    this.valueAccessor = config.valueAccessor || ((d: any) => d.value);
    this.data = config.data.map((d) => ({ ...d }));
    this.markRadius = config.markRadius || 3;
    this.markPadding = 1;
    this.color = config.color || (() => 'steelblue');

    const xMin = config.min !== undefined ? config.min : d3.min(this.data, this.valueAccessor) || 0;
    const xMax = config.max !== undefined ? config.max : d3.max(this.data, this.valueAccessor) || 0;
    this.xScale = d3
      .scaleLinear()
      .domain([xMin, xMax])
      .range([0, this.chartSize.width]);

    this.normalizeScale = d3
      .scaleLinear()
      .domain([xMin, xMax])
      .range([0, 1]);

    this.xAxis = d3.axisBottom(this.xScale).tickSize(-this.chartSize.height);

    this.xAxisSelection = this.chartSelection
      .append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${this.chartSize.height / 2})`);

    this.setSimulation();
    // const { valueMatrix, nestedLinks, nodeIndexMap } = this.transformData(config.links, config.nodes);
    this.runSimulation();
    this.draw();
    if (config.focalId) {
      this.focus(config.focalId);
    }
  }

  setSimulation = () => {
    const self = this;
    this.simulation = d3
      .forceSimulation<ISimulationNodeDatum>(self.data) //.map((d) => ({ ...d, x: self.xScale(d.value) })))
      .force(
        'x',
        d3
          .forceX<ISimulationNodeDatum>((d) => self.xScale(self.valueAccessor(d)))
          .strength(0.9)
      )
      .force('y', d3.forceY<ISimulationNodeDatum>(self.chartSize.height / 2).strength(0.1))
      .force(
        'collide',
        d3
          .forceCollide<ISimulationNodeDatum>((d) => this.nodeRadius(d))
          .iterations(5)
      );

    // .stop();
  };

  runSimulation() {
    this.simulation.restart();
    for (let i = 0; i < 40; i++) {
      this.simulation.tick();
    }
  }

  draw(): void {
    const self = this;
    this.svg
      .insert('line', 'g')
      .attr('x1', 0)
      .attr('x2', this.size.width)
      .attr('y1', this.chartSize.height / 2 + this.margin.top)
      .attr('y2', this.chartSize.height / 2 + this.margin.top)
      .attr('stroke-width', '2px')
      .attr('stroke', '#f8efe6');

    this.svg
      .insert('line', 'g')
      .attr('x1', 1)
      .attr('x2', 1)
      .attr('y1', this.chartSize.height / 2 + this.margin.top - 4)
      .attr('y2', this.chartSize.height / 2 + this.margin.top + 4)
      .attr('stroke-width', '2px')
      .attr('stroke', '#f8efe6');

    this.svg
      .insert('line', 'g')
      .attr('x1', this.size.width - 1)
      .attr('x2', this.size.width - 1)
      .attr('y1', this.chartSize.height / 2 + this.margin.top - 4)
      .attr('y2', this.chartSize.height / 2 + this.margin.top + 4)
      .attr('stroke-width', '2px')
      .attr('stroke', '#f8efe6');

    this.swarmSelection = this.chartSelection
      .selectAll('g.node')
      .data(this.simulation.nodes())
      .join('g')
      .attr('class', 'node cursor-pointer')
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .on('mouseover', function(event: any, d: any) {
        self.handleNodeMouseover(this as SVGElement, d);
      })
      .on('mouseout', function(event, d) {
        self.handleNodeMouseout(this as SVGElement);
      })
      .on('click', function(event, d) {
        self.handleNodeClick(this as SVGElement, d);
      });

    this.swarmSelection
      .append('circle')
      .attr('r', this.markRadius)
      .attr('fill', (d) => this.color(d, this))
      .attr('stroke', 'white')
      .attr('stroke-width', 2);
  }

  updateData(data: IDatum[]): void {
    this.data = data;
    // this.setSimulation();
    // this.runSimulation();
    this.swarmSelection = this.chartSelection
      .selectAll('g.node')
      .data(this.simulation.nodes())
      .join('g')
      .attr('class', 'node');

    this.swarmSelection.transition().attr('transform', (d) => `translate(${d.x}, ${d.y})`);

    this.swarmSelection.select('circle').attr('r', this.nodeRadius);
  }

  focus(id: string): void {
    this.focalId = id;
    this.focalNode = this.data.find((d) => d.id === id) || null;
    // this.setSimulation();
    // this.runSimulation();
    if (this.swarmSelection) {
      this.swarmSelection.attr('transform', (d) => `translate(${d.x}, ${d.y})`);
      this.swarmSelection
        .select('circle')
        .attr('r', this.nodeRadius)
        .attr('fill', (d) => this.color(d, this));
      this.swarmSelection.filter((d) => d.id === id).raise();
    }
    if (this.focalNode && this.swarmSelection) {
      this.swarmSelection
        .filter((d) => d.id === id)
        .select('circle')
        .attr('r', (d) => this.nodeRadius(d) * 2);
    }
  }

  nodeRadius = (d: IDatum): number => {
    if (this.focalNode?.id === d.id) {
      return this.markRadius; // * 3;
    }
    return this.markRadius;
  };

  handleNodeClick(eventTarget: SVGElement, d: ISimulationNodeDatum): void {
    if (this.onNodeClick) {
      this.onNodeClick(d, this);
    }
  }

  handleNodeMouseover(eventTarget: SVGElement, d: ISimulationNodeDatum): void {
    const x = d.x || 0;
    const y = d.y || 0;

    this.svg.classed('hovered', true);
    d3.select(eventTarget).classed('hovered', true);
    if (this.nodeTooltip) {
      this.nodeTooltip(this.tooltip, d, this);
    } else {
      keyValueTitleTooltip(this.tooltip, d.label, {
        // @ts-ignore
        value: this.valueAccessor(d),
      });
    }

    placeTooltip(this.tooltip, d3.select(this.target), {
      x: this.margin.left + x,
      y: this.margin.top + y - this.markRadius - 4,
      position: 'relative',
      fitInContainer: false,
    });
    this.tooltip.style('visibility', 'visible');
  }

  handleNodeMouseout(eventTarget: SVGElement): void {
    this.svg.classed('hovered', false);
    d3.select(eventTarget).classed('hovered', false);
    this.tooltip.style('visibility', 'hidden');
  }

  exportToPng(): void {
    const customCss = `text { font-family: 'Tiempos Text', sans-serif }`;
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      fileName: this.exportFileName,
      title: this.exportTitle || undefined,
    });
  }
}
